import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment'; // NOSONAR: This import is necessary for moment usage. Default import doesn't work.
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, catchError, shareReplay, tap } from 'rxjs/operators';

import { UserInfo } from '../model/user-info.model';
import { AppConfiguration } from '../../core/models/app-configuration.model';
import { ConversionStrategy, RecognitionStatus } from '../constants/user.constants';

/**
 * User Info Service
 * @description Angular service used for retrieve and update user information
 */
@Injectable({ providedIn: 'root' })
export class PecPlusService {
  private userInfoCache$?: Observable<UserInfo | null>;
  private readonly userInfoState = new ReplaySubject<UserInfo | null>(1);
  private userInfo: UserInfo | null = null;

  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfiguration) { }

  getResourceUrl(): string {
    return this.appConfig.serverApiUrl + '/api/v1/user/info';
  }

  setUserInfo(newUserInfo: UserInfo | null): void {
    this.userInfo = newUserInfo;
    const isConversionStrategyTwoThreeOrFour = this.userInfo?.conversionStrategy === ConversionStrategy.TWO || this.userInfo?.conversionStrategy === ConversionStrategy.THREE || this.userInfo?.conversionStrategy === ConversionStrategy.FOUR;
    if (
      isConversionStrategyTwoThreeOrFour &&
      this.userInfo?.recognitionStatus === RecognitionStatus.OK &&
      this.appConfig.conversionStrategy1OK
    ) {
      this.userInfo.conversionStrategy = ConversionStrategy.ONE;
    }

    /* mock
    this.userInfo!.conversionStrategy = 3;
    this.userInfo!.recognitionStatus = 'NotIdentified';*/
    this.userInfoState.next(this.userInfo);
  }

  /**
   *  Get userInfo visualize beta
   * @param userInfo
   * @returns
   */
  getUserInfoVisBeta(userInfo: UserInfo | null): boolean {
    this.userInfo = userInfo;
    if (this.appConfig.beta === '') {
      return true;
    }
    if (!userInfo?.beta) {
      return false;
    }
    const arrayBeta = userInfo.beta.split(',');
    return (arrayBeta || []).includes(this.appConfig.beta);
  }

  /**
   * Get current user informations
   */
  getUserInfo(): Observable<UserInfo | null> {
    this.userInfoCache$ = this.fetch().pipe(
      catchError(() => of(null)),
      tap((userInfoValue: UserInfo | null) => {
        this.setUserInfo(userInfoValue);
      }),
      shareReplay()
    );
    return this.userInfoCache$;
  }

  private fetch(): Observable<UserInfo> {
    return this.http.get<UserInfo>(this.getResourceUrl()).pipe(map((res: UserInfo) => this.convertDateArrayFromServer(res)));
  }

  getUserInfoState(): Observable<UserInfo | null> {
    return this.userInfoState.asObservable();
  }

  /**
   * @ignore
   */
  protected convertDateArrayFromServer(res: UserInfo): UserInfo {
    if (res) {
      res.passwordExpirationTime = res.passwordExpirationTime ? moment(res.passwordExpirationTime) : null;
    }
    return res;
  }
}
